/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from "@hookform/resolvers/yup"
import {
  ActionButton,
  Button,
  DateInput,
  Input,
  NumberInput,
  Select
} from "components"
import { Fragment, useEffect, useMemo } from "react"
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form"
import { TbEye } from "react-icons/tb"
import { DataBoqType, validationBoqSchema } from "../types/BoqTypes"
import moment from "moment"
import { useVendorList } from "../utils/useVendorList"
import { FormStateProjectProfile } from "../types/ProfileTypes"
import { useToggle } from "utils"
import { PROJECT_STATUS_OPTIONS } from "../utils/projectTypeOption"

type Action = "add" | "edit"

type ItemProps = {
  index?: number
  action: Action
  defaultValues?: DataBoqType
  isHideForm?: boolean
  onCancelEditing?: () => void
  onDeleteData?: () => void
  onSubmitData: (value: DataBoqType) => void
  vendor: ReturnType<typeof useVendorList>
  dataProject: FormStateProjectProfile
}

export function TableItem(props: ItemProps) {
  const isHideForm = useToggle(props.isHideForm ?? true)

  const defaultValues: DataBoqType = useMemo(
    () => ({
        project_id: props?.dataProject.project_id ?? 0,
        boq_name: props?.defaultValues?.boq_name ?? "",
        description: props?.defaultValues?.description ?? "",
        vendor_id: props?.defaultValues?.vendor_id ?? 0,
        vendor_name: props?.defaultValues?.vendor_name ?? '',
        qty: props?.defaultValues?.qty ?? 0,
        mr_unit_id: props?.defaultValues?.mr_unit_id ?? 0,
        price: props?.defaultValues?.price ?? 0,
        budget: props?.defaultValues?.budget ?? 0,
        start_date: props?.defaultValues?.start_date ?? "",
        due_date : props?.defaultValues?.due_date ?? "",
        project_boq_id: props?.defaultValues?.project_boq_id ?? null,
        status: props?.defaultValues?.status ?? 1,
        actual: props?.defaultValues?.actual ?? 0,
        variance: props?.defaultValues?.variance ?? 0,
    }),
    [props.defaultValues]
  )

  const methods = useForm<DataBoqType>({
    defaultValues,
    resolver: yupResolver(validationBoqSchema)
  })

  useEffect(() => methods.reset(defaultValues), [defaultValues])

  return (
    <FormProvider {...methods}>
      <tr className="no-padding-body">
        <td className="text-center">
          {isHideForm.isActive && (
            <Button
              color="transparent"
              onClick={isHideForm.toggle}
              permission="AT021">
              <TbEye className="size-5" />
            </Button>
          )}
        </td>
        {isHideForm.isActive ? (
          <td colSpan={12} />
        ) : (
          <Fragment>
            <td>
              <Controller
                control={methods.control}
                name="boq_name"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    placeholder="Enter BOQ Name"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </td>
            <td>
              <Controller
                control={methods.control}
                name="description"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    placeholder="Enter Description"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </td>
            <td><Vendor vendor={props.vendor}/></td>
            <td>
              <Controller
                control={methods.control}
                name="budget"
                render={({ field, fieldState }) => (
                  <NumberInput
                    textRight
                    value={field.value}
                    onValueChange={(value) => {
                        if (value?.floatValue == null || isNaN(value.floatValue)) {
                            field.onChange(0)
                        } else {
                            field.onChange(value.floatValue)
                        }
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </td>
            <td>
                <Controller
                    control={methods.control}
                    name="actual"
                    render={({ field }) => (
                        <div>{field.value}</div>
                    )}
                />
            </td>
            <td>
                <Controller
                    control={methods.control}
                    name="variance"
                    render={({ field }) => (
                        <div>{field.value ? `${field.value}%` : ''}</div>
                    )}
                />
            </td>
            <td>
                <Controller
                    control={methods.control}
                    name="start_date"
                    render={({ field, fieldState }) => (
                        <DateInput
                            placeholderText="Enter Start Date"
                            error={fieldState.error?.message}
                            selected={field.value ? moment(field.value).toDate() : null }
                            onChange={(value) =>
                                field.onChange(moment(value).format("YYYY-MM-DD"))
                            }
                        />
                    )}
                />
            </td>
            <td>
                <Controller
                    control={methods.control}
                    name="due_date"
                    render={({ field, fieldState }) => (
                        <DateInput
                            placeholderText="Enter Due Date"
                            error={fieldState.error?.message}
                            selected={field.value ? moment(field.value).toDate() : null }
                            onChange={(value) =>
                                field.onChange(moment(value).format("YYYY-MM-DD"))
                            }
                        />
                    )}
                />
            </td>
            <td>
              <div className="flex flex-col gap-1">
                {(props.defaultValues?.project_file ?? []).map((file: any) => (
                  <a
                    key={file.project_file_id}
                    href={`${process.env.REACT_APP_BASE_URL_API}/getimage/?imgpath=${file.path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline text-sm truncate max-w-[160px] block"
                    title={file.project_file_name}
                  >
                    {file.project_file_name}
                  </a>
                ))}
              </div>
            </td>
            <td>
              {PROJECT_STATUS_OPTIONS.find(o => Number(o.value) === (props.defaultValues as any)?.checklist_status)?.label ?? '-'}
            </td>
            <td className="text-center">
              {(props.defaultValues as any)?.checklist_date
                ? moment((props.defaultValues as any).checklist_date).format('DD/MM/YYYY')
                : '-'}
            </td>
            <td className="text-center">
              <section className="flex justify-center items-center">
                {props.action === "edit" && (
                  <ActionButton.Close
                    permission="AT021"
                    onClick={props?.onCancelEditing}
                  />
                )}

                <ActionButton.Add
                  permission="AT021"
                  onClick={() => {
                    methods.handleSubmit((data) =>
                      props.onSubmitData(data)
                    )()
                  }}
                />

                {props.action === "add" && (
                  <ActionButton.Close
                    permission="AT021"
                    onClick={isHideForm.toggle}
                  />
                )}
              </section>
            </td>
          </Fragment>
        )}
      </tr>
    </FormProvider>
  )
}

function Vendor({ vendor }: Pick<ItemProps, "vendor">) {
  const { dropdown } = vendor
  const { control, setValue } = useFormContext<DataBoqType>()

  return (
    <Fragment>
      <Controller
        control={control}
        name="vendor_id"
        render={({ field, fieldState }) => {
          return (
            <section>
              <Select
                placeholder="Select Vendor"
                error={fieldState.error?.message}
                options={dropdown}
                value={
                  field.value
                    ? dropdown.find((item) => item.vendor_id === field.value)
                    : null
                }
                onChange={(e) => {
                    field.onChange(e?.value)
                    setValue("vendor_name", e?.label ?? "")
                }}
              />
            </section>
          )
        }}
      />
    </Fragment>
  )
}

